import React, { forwardRef, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';
import _ from 'lodash';

const MicrosoftDefenderMappingForm = forwardRef(function MicrosoftDefenderMappingForm({ disableProjectFields = false, ...props }, ref) {
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [form] = Form.useForm();
  const IntRef = useRef({})
  const [mappedSubscriptionList] = useState(() => {
    // this state contains the already mapped Resources
    // This is stored because the Resources dropdown is paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if (props.record) {
      const { resourceGroupId, resourceGroupName } = props.record;
      if(resourceGroupId) {
        const resourceGroupIdArray = resourceGroupId.split(";");
        const resourceGroupNameArray = resourceGroupName.split(";");
        return {
          resources: resourceGroupIdArray.map((id, index) => ({ id, name: resourceGroupNameArray[index]}))
        }
      }else {
        return { resources: [] }
      }
    }
    return { resources: [] }
  })

  const [options, setOptions] = useState([]);

  const setOptionsFromPagination = useCallback((option) => {
    setOptions(prev => _.uniqBy([...prev, ...option], 'id'));
  }, [])

  // Fetch Subscription list
  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      setSubscriptionList(projects);
    }).catch((err) => {
      message.error('Problem fetching projects')
    });
    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchResources = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);
    promise.then((data) => {
      // merging the already mapped resources group to the fetched list so that if the selected value is not in the option list
      // then id should not be displayed
      if (mappedSubscriptionList["resources"].length > 0 && !_.isEmpty(data.projects)) {
        data.projects = [...data.projects, ...mappedSubscriptionList["resources"]];
      }
      setOptionsFromPagination(data.projects)
    })
    return { promise, cancel }
  }, [props.toolName, setOptionsFromPagination, mappedSubscriptionList]);


  const getExtraFieldsData = useCallback((config) => {
    const { subscriptionId, resourceGroupId, isAllResourceGroups } = config;

    const selectedSubscription = subscriptionList.find(sub => sub.id === subscriptionId);
    
    const { name, otherProperties: { resourceGroupCount }} = selectedSubscription

    if(!resourceGroupCount){
      const errorMessage = "No Resource Group available"
      message.error(errorMessage);
      throw new Error(errorMessage);
    }
    
    if (_.isEmpty(resourceGroupId) && !isAllResourceGroups) {
      const errorMessage = "Please select a Resource Group from the dropdown or Select All Resource Groups to save the mapping"
      message.error(errorMessage);
      throw new Error(errorMessage);
    }

    let data;
    if(!isAllResourceGroups) {
      data = options.reduce(
        (acc, cv) => {
          const { id, name } = cv;
          if (resourceGroupId.includes(id)) {
            acc.id.push(id);
            acc.name.push(name);
          }
          return acc;
        },
        { id: [], name: [] }
      );
    }
  
    return ({
      subscriptionId,
      subscriptionName: name,
      isAllResourceGroups,
      ...(!isAllResourceGroups && { resourceGroupId: data.id.join(';'), resourceGroupName: data.name.join(';') })
    })
  }, [subscriptionList, options])
   
  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { resourceGroupId } = config;
    return resourceGroupId ? { resourceGroupId: resourceGroupId.split(";"), isAllResourceGroups: false }: { };
  }, []);

  const onSubscriptionChange = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      subscriptionId: value,
      resourceGroupId: undefined
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return (!fieldConfiguration || !fieldConfiguration.subscriptionId || fieldConfiguration.isAllResourceGroups || disableProjectFields );
  }, [disableProjectFields]);

  const extraFields = useMemo(() => ([
    {
      name: "subscriptionId",
      label: "Subscription Name",
      type: FieldTypes.SELECT,
      colSpan: 8,
      options: parseSubscriptionSelectOptions(subscriptionList),
      disabled: disableProjectFields,
      props: {
        autoFocus: !disableProjectFields,
        onChange: onSubscriptionChange
      },
      required: true
    },
    {
      name: "resourceGroupId",
      label: "Resource Group",
      colSpan: 8,
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form) }),
      props: {
        mode: "multiple",
        setExtraProps: (field, listField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const subscriptionId = allConfigs[listField.name]?.subscriptionId;
          if (!IntRef.current[subscriptionId]) {
            IntRef.current[subscriptionId] = { login_id: props.loginConfigId, organisation: subscriptionId };
          }
          return {
            api: subscriptionId && fetchResources,
            apiParams: IntRef.current[subscriptionId],
            resetOptionsProp: subscriptionId,
            parseSelectOptions: customParseSelectOptionsOptions,
          }
        }
      },
    },
    {
      name: "isAllResourceGroups",
      label: "Select All Resource Group",
      type: FieldTypes.BOOLEAN,
      colSpan: "8",
      inline: true,
      disabled: disableProjectFields,
      props: {
        style: {marginTop: "14%", marginBottom:"auto"}
      },
    }
  ]), [subscriptionList, onSubscriptionChange, props.loginConfigId, isProjectFieldDisabled,disableProjectFields, fetchResources, form]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      extraFields={extraFields}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  )
})

export default MicrosoftDefenderMappingForm;

function customParseSelectOptionsOptions(options) {
  return options.map(option => ({ label: _.last(option.id.split('/')), value: option.id}))
}

function parseSubscriptionSelectOptions(options){
  return _.map(options, (option) => {
      let { id, name, otherProperties :{ resourceGroupCount }} = option;
      return { label: `${name} -[${resourceGroupCount}]` , value: id };
  })
}
